import React, { useState, useCallback, useRef } from 'react';
import Cropper from 'react-easy-crop';
import { toast } from 'react-toastify';
import axios from 'axios';
import { 
  PROFILE_UPLOAD_STATUS_UPLOADING, 
  PROFILE_UPLOAD_STATUS_UPLOADED, 
  PROFILE_UPLOAD_STATUS_UPLOAD_FAILED, 
  PROFILE_UPLOAD_STATUS_NOT_SELECTED, 
  PROFILE_UPLOAD_STATUS_GETUPLOADDATA_FAILED, 
  PROFILE_UPLOAD_STATUS_RESTORE_FAILED,
  MAX_FILE_SIZE_UPLOAD,
  MAX_FILE_SIZE_COMPRESSION,
  UPLOAD_TIMEOUT,
  FILE_SIZE_EXCEEDED_MESSAGE
} from '../../constants/Constants';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import RefreshIcon from '@mui/icons-material/Refresh';
import { 
  Grid, 
  IconButton, 
  Box, 
  CircularProgress,
  Typography,
  Paper,
  Fade,
  Button
} from '@mui/material';
import { UPLOAD_DATA_FAILED_MESSAGE } from './ProfileContext';

const ImageUploader = ({ 
  fetchImageUploadData,
  profileImageUploadUrls, 
  setProfileImageUploadStatuses, 
  localProfileImageUrls,
  setLocalProfileImageUrls,
  profileImageUploadStatuses,
  profileUploadDataKeys,
  setProfileUploadDataKeys,
  setProfileImageUploadUrls
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showCropper, setShowCropper] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const fileInputRef = useRef(null);

  React.useEffect(() => {
    fetchImageUploadData(true);
  }, []);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFileSelect = (event) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      if (!file.type.startsWith("image/")) {
        // Optionally, show a toast or alert here
        alert("Please select a valid image file.");
        return;
      }
      
      // Check file size
      if (file.size > MAX_FILE_SIZE_UPLOAD) {
        alert(FILE_SIZE_EXCEEDED_MESSAGE);
        return;
      }
      
      setSelectedFile(file);
      const url = URL.createObjectURL(file);
      setImageUrl(url);
      setShowCropper(true);
    }
  };

  const handleImageClick = (index) => {
    setCurrentImageIndex(index);
    fileInputRef.current?.click();
  };

  const handleDelete = (index, event) => {
    event.stopPropagation(); // Prevent triggering the image click
    // Shift all images to the left
    const newStatuses = [...profileImageUploadStatuses];
    const newUrls = [...localProfileImageUrls];
    const newKeys = [...profileUploadDataKeys];
    const newUploadUrls = [...profileImageUploadUrls];
    
    const deletedImageKey = profileUploadDataKeys[index];
    const deletedUploadUrl = profileImageUploadUrls[index];
    
    // Revoke the URL of the deleted image if it's a blob URL
    if (newUrls[index] && newUrls[index].startsWith('blob:')) {
      URL.revokeObjectURL(newUrls[index]);
    }
    
    // Move all images after the deleted one one position left
    for (let i = index; i < profileImageUploadStatuses.length - 1; i++) {
      newStatuses[i] = newStatuses[i + 1];
      newUrls[i] = newUrls[i + 1];
      newKeys[i] = newKeys[i + 1];
      newUploadUrls[i] = newUploadUrls[i + 1];
    }
    
    // Set the last position to empty
    newStatuses[newStatuses.length - 1] = PROFILE_UPLOAD_STATUS_NOT_SELECTED;
    newUrls[newStatuses.length - 1] = "";
    newKeys[newStatuses.length - 1] = deletedImageKey;
    newUploadUrls[newStatuses.length - 1] = deletedUploadUrl;
    
    setProfileImageUploadStatuses(newStatuses);
    setLocalProfileImageUrls(newUrls);
    setProfileUploadDataKeys(newKeys);
    setProfileImageUploadUrls(newUploadUrls);
    toast.success('Image deleted successfully');
  };
  const compressImage = (blob, maxSize = MAX_FILE_SIZE_COMPRESSION) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        let width = img.width;
        let height = img.height;
        let quality = 0.9;
        
        // Store original dimensions
        const minOriginalWidth = width/10;
        const minOriginalHeight = height/10;
        
        // Start with original dimensions
        canvas.width = width;
        canvas.height = height;
        
        const tryCompress = () => {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          
          canvas.toBlob((compressedBlob) => {
            if (!compressedBlob) return resolve(blob); // fallback

            if (compressedBlob.size <= maxSize) {
              resolve(compressedBlob);
            } else if (quality > 0.1) {
              quality -= 0.1;
              tryCompress(); // retry with lower quality
            } else {
              // resize and reset quality, but check minimum dimensions
              const newWidth = Math.floor(width * 0.9);
              const newHeight = Math.floor(height * 0.9);
              
              // Stop if we've reached minimum dimensions
              if (newWidth < minOriginalWidth || newHeight < minOriginalHeight) {
                resolve(compressedBlob); // return the best we can do
                return;
              }
              
              width = newWidth;
              height = newHeight;
              canvas.width = width;
              canvas.height = height;
              quality = 0.9;
              tryCompress(); // restart with resized image
            }
          }, 'image/jpeg', quality);
        };
        tryCompress();
      };
      
      img.src = URL.createObjectURL(blob);
    });
  };
  
  const getCroppedImg = async (imageSrc, pixelCrop) => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, 'image/jpeg');
    });
  };

  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (error) => reject(error));
      image.src = url;
    });

  const handleCropComplete = async () => {
    if (!selectedFile || !croppedAreaPixels || !imageUrl) return;

    try {
      const croppedImage = await getCroppedImg(imageUrl, croppedAreaPixels);
      
      // Compress the image if it's larger than the compression limit
      let finalImage = croppedImage;
      
      if (croppedImage.size > MAX_FILE_SIZE_COMPRESSION) {
        finalImage = await compressImage(croppedImage, MAX_FILE_SIZE_COMPRESSION);
      }
      
      const file = new File([finalImage], selectedFile.name, { type: 'image/jpeg' });
      
      // Upload the cropped and compressed image
      const uploadUrl = profileImageUploadUrls[currentImageIndex];
      if (!uploadUrl) {
        throw new Error('No upload URL available');
      }

      setProfileImageUploadStatuses(prev => {
        const newStatuses = [...prev];
        newStatuses[currentImageIndex] = PROFILE_UPLOAD_STATUS_UPLOADING;
        return newStatuses;
      });

      const response = await axios.put(uploadUrl, file, { timeout: UPLOAD_TIMEOUT });
      
      if (response.status === 200) {
        setProfileImageUploadStatuses(prev => {
          const newStatuses = [...prev];
          newStatuses[currentImageIndex] = PROFILE_UPLOAD_STATUS_UPLOADED;
          return newStatuses;
        });
        setLocalProfileImageUrls(prev => {
          const newUrls = [...prev];
          // Revoke the old URL if it exists and is a blob URL
          if (newUrls[currentImageIndex] && newUrls[currentImageIndex].startsWith('blob:')) {
            URL.revokeObjectURL(newUrls[currentImageIndex]);
          }
          newUrls[currentImageIndex] = URL.createObjectURL(file);
          return newUrls;
        });
        toast.success('Image uploaded successfully');
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      setProfileImageUploadStatuses(prev => {
        const newStatuses = [...prev];
        newStatuses[currentImageIndex] = PROFILE_UPLOAD_STATUS_UPLOAD_FAILED;
        return newStatuses;
      });
      toast.error('Failed to upload image');
    } finally {
      // Reset all crop-related state
      setShowCropper(false);
      setSelectedFile(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
      setImageUrl(null);
    }
  };

  const renderImageCard = (index) => {
    const status = profileImageUploadStatuses[index];
    const imageUrl = localProfileImageUrls[index];

    return (
      <Paper
        elevation={2}
        sx={{
          position: 'relative',
          aspectRatio: '3/4',
          cursor: 'pointer',
          overflow: 'hidden',
          '&:hover .overlay': {
            opacity: 1
          }
        }}
        onClick={() => handleImageClick(index)}
      >
        {status === PROFILE_UPLOAD_STATUS_UPLOADED ? (
          <>
            <Box
              component="img"
              src={imageUrl}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
              alt={`Uploaded ${index + 1}`}
            />
            <Fade in={true}>
              <Box
                className="overlay"
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  background: 'linear-gradient(to top, rgba(0,0,0,0.6), transparent)',
                  opacity: 0,
                  transition: 'opacity 0.2s',
                  p: 2,
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 1
                }}
              >
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleImageClick(index);
                  }}
                  sx={{ bgcolor: 'rgba(255,255,255,0.9)', '&:hover': { bgcolor: 'white' } }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  onClick={(e) => handleDelete(index, e)}
                  sx={{ bgcolor: 'rgba(255,255,255,0.9)', color: 'error.main', '&:hover': { bgcolor: 'white' } }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Fade>
          </>
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              color: 'text.secondary'
            }}
          >
            {status === PROFILE_UPLOAD_STATUS_UPLOADING ? (
              <>
                <CircularProgress size={40} />
                <Typography>Uploading...</Typography>
              </>
            ) : status === PROFILE_UPLOAD_STATUS_UPLOAD_FAILED ? (
              <>
                <Typography color="error">Upload Failed</Typography>
                <Typography variant="body2">Click to retry</Typography>
              </>
            ) : status === PROFILE_UPLOAD_STATUS_RESTORE_FAILED ? (
              <>
                <Typography color="error">Failed to restore current Image</Typography>
                <Typography variant="body2">Click to upload</Typography>
              </>
            ) : (
              <>
                <AddPhotoAlternateIcon sx={{ fontSize: 40 }} />
                <Typography>Click to upload</Typography>
              </>
            )}
          </Box>
        )}
      </Paper>
    );
  };

  const isUploading = profileImageUploadStatuses.some(status => status === PROFILE_UPLOAD_STATUS_UPLOADING);
  return (
    <Box sx={{ width: '100%' }}>
      {/* File size limit info */}
      <Typography 
        variant="body2" 
        color="red" 
        sx={{ 
          mb: 2, 
          textAlign: 'left',
          fontStyle: 'italic',
          fontWeight: 'bold'
        }}
      >
        * Maximum file size: 10MB
      </Typography>
      
      {profileImageUploadStatuses.some(status => status === PROFILE_UPLOAD_STATUS_GETUPLOADDATA_FAILED) ? (
        <Paper
          elevation={2}
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            textAlign: 'center',
            bgcolor: 'background.paper'
          }}
        >
          <Typography color="error" variant="h6">
            {UPLOAD_DATA_FAILED_MESSAGE}
          </Typography>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={() => fetchImageUploadData()}
            sx={{ mt: 2 }}
          >
            Retry Upload
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {profileImageUploadStatuses.map((_, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              {renderImageCard(index)}
            </Grid>
          ))}
        </Grid>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {showCropper && imageUrl && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            bgcolor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1300
          }}
        >
          <Paper sx={{ p: 2, maxWidth: 'md', width: '100%' }}>
            <Box sx={{ height: 384, position: 'relative' }}>
              <Cropper
                image={imageUrl}
                crop={crop}
                zoom={zoom}
                aspect={3/4}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
              />
            </Box>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button
                onClick={() => {
                  setShowCropper(false);
                  setSelectedFile(null);
                  if (imageUrl) {
                    URL.revokeObjectURL(imageUrl);
                  }
                  setImageUrl(null);
                }}
                variant="outlined"
                color="inherit"
                disabled={isUploading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCropComplete}
                variant="contained"
                color="primary"
                disabled={isUploading}
              >
                {isUploading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Uploading...
                  </>
                ) : (
                  'Done'
                )}
              </Button>
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default ImageUploader; 